import React from 'react';
import { useFragment } from 'react-relay/hooks';

import { createStyles, WithStyles, withStyles } from '@material-ui/core';
import TableRow, { TableRowProps } from '@material-ui/core/TableRow';
import { graphql } from 'babel-plugin-relay/macro';
import { get } from 'lodash';
// @ts-ignore
import moment from 'moment-timezone';

import { useCurrentUser } from '@lib/hooks';

import { TableCell } from 'components/v2';
import { IconButtonReminderActionsActionKey } from 'components/v2/IconButtonReminderActions';
import { getFirstActiveContact } from 'ui-utils';

import { TableRowReminder_Reminder$key } from './__generated__/TableRowReminder_Reminder.graphql';
import IconButtonReminderActions from './IconButtonReminderActions';
import TypographyReminderType from './TypographyReminderType';

const styles = () =>
  createStyles({
    cellDate: {
      width: 175,
    },
    cellActions: {
      width: 60,
    },
  });

export interface TableRowReminderProps extends Omit<TableRowProps, 'classes'>, WithStyles<typeof styles> {
  fragmentRef: TableRowReminder_Reminder$key;
}

const TableRowReminder: React.FC<TableRowReminderProps> = ({ classes, fragmentRef, ...rest }) => {
  const reminder = useFragment<TableRowReminder_Reminder$key>(
    graphql`
      fragment TableRowReminder_Reminder on Reminder {
        _id
        sendAt
        patient {
          _id
          name
          contact {
            active
            type
            value
          }
          doNotContact
        }
        appointment {
          date
          provider {
            name
          }
        }
        ...TypographyReminderType_Reminder
        ...IconButtonReminderActions_Reminder
      }
    `,
    fragmentRef,
  );

  const contact = getFirstActiveContact(reminder.patient);
  const { appointment } = reminder;

  const currentUser = useCurrentUser();

  return (
    <TableRow {...rest}>
      {/* date */}
      <TableCell className={classes.cellDate} noWrap>
        {!!reminder.sendAt && moment(reminder.sendAt as string).format('lll')}
      </TableCell>
      {/* patient */}
      <TableCell noWrap href={`/patients/${get(reminder, 'patient._id')}/chat`}>
        {get(reminder, 'patient.name')}
      </TableCell>
      {/* destination */}
      <TableCell noWrap>{get(contact, 'value')}</TableCell>
      {/* appointment date */}
      <TableCell noWrap href={appointment ? `/patients/${get(reminder, 'patient._id')}/appointments` : undefined}>
        {appointment
          ? moment(appointment.date as string)
              .tz(currentUser!.settings.timezone)
              .format('LLL')
          : '-'}
      </TableCell>
      {/* appointment provider */}
      <TableCell noWrap>{get(appointment, 'provider.name', '-')}</TableCell>
      {/* type */}
      <TableCell noWrap>
        <TypographyReminderType fragmentRef={reminder} variant="inherit" />
      </TableCell>
      {/* actions */}
      <TableCell className={classes.cellActions} padding="none">
        <IconButtonReminderActions
          fragmentRef={reminder}
          actions={[
            IconButtonReminderActionsActionKey.Send,
            IconButtonReminderActionsActionKey.GoToPatient,
            IconButtonReminderActionsActionKey.GoToAppointment,
          ]}
        />
      </TableCell>
    </TableRow>
  );
};

export default withStyles(styles)(TableRowReminder);
